"use client";

import { forwardRef, type ReactNode, useRef } from "react";

import { cn } from "@/lib/utils";

/* ── Icons ── */

const PaperclipIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/* ── Types ── */

export interface FileSelectProps {
  /** Display label for the button when no file is selected */
  label?: string;
  /** Display label for the button when a file is selected */
  changeLabel?: string;
  /** Currently selected file name */
  fileName?: string;
  /** Hint text shown when no file is selected */
  hint?: string;
  /** accept attribute for the file input */
  accept?: string;
  /** Called when a file is selected via click */
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called when the clear button is clicked */
  onClear?: () => void;
  /** Error message */
  error?: string;
  /** Disable the file select */
  disabled?: boolean;
  /** Additional content (e.g., download link for locked state) */
  children?: ReactNode;
  className?: string;
  /** data-testid for the hidden input */
  testId?: string;
}

/* ── Component ── */

const FileSelect = forwardRef<HTMLInputElement, FileSelectProps>(
  (
    {
      label = "파일 선택",
      changeLabel = "파일 변경",
      fileName,
      hint,
      accept,
      onFileChange,
      onClear,
      error,
      disabled = false,
      children,
      className,
      testId,
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? internalRef;

    const hasFile = !!fileName;

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <div className="flex items-center gap-2">
          {children ?? (
            <>
              <button
                type="button"
                disabled={disabled}
                className={cn(
                  "inline-flex items-center gap-2",
                  "px-3.5 py-[7px] rounded-4",
                  "border-2 bg-white",
                  "text-[14px] font-medium",
                  "cursor-pointer whitespace-nowrap",
                  "transition-colors duration-150",
                  hasFile
                    ? "border-primary-700 bg-primary-100 text-primary-700 hover:bg-primary-200"
                    : "border-gray-300 text-gray-700 hover:border-gray-500 hover:bg-gray-100",
                  disabled &&
                    "opacity-60 cursor-default pointer-events-none"
                )}
                onClick={() => inputRef.current?.click()}
              >
                <PaperclipIcon />
                {hasFile ? changeLabel : label}
              </button>

              {hasFile ? (
                <span className="text-[14px] font-medium text-gray-900 truncate max-w-[200px]">
                  {fileName}
                </span>
              ) : (
                hint && (
                  <span className="text-[13px] text-gray-600">{hint}</span>
                )
              )}

              {hasFile && onClear && !disabled && (
                <button
                  type="button"
                  className="flex items-center justify-center w-5 h-5 rounded-full text-gray-500 hover:text-danger-500 hover:bg-gray-200 transition-colors"
                  onClick={() => {
                    onClear();
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                >
                  <CloseIcon />
                </button>
              )}
            </>
          )}
        </div>

        {error && (
          <p className="text-[13px] text-danger-500">{error}</p>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onFileChange}
          className="hidden"
          data-testid={testId}
        />
      </div>
    );
  }
);
FileSelect.displayName = "FileSelect";

export { FileSelect };
